import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/currency';
import type { ParticipantBalance, GroupBalance, CurrencyCode } from '../../types';
import { cn } from '../../lib/utils';

interface BalanceSummaryProps {
  participantBalances: ParticipantBalance[];
  groupBalances: GroupBalance[];
  currency: CurrencyCode;
}

export const BalanceSummary = memo(function BalanceSummary({
  participantBalances,
  groupBalances,
  currency,
}: BalanceSummaryProps) {
  // Sort by balance (most positive first)
  const sortedParticipants = [...participantBalances].sort(
    (a, b) => b.balance - a.balance
  );
  const sortedGroups = [...groupBalances].sort((a, b) => b.totalBalance - a.totalBalance);

  return (
    <div className="space-y-4">
      {/* Group balances */}
      {sortedGroups.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Balance por grupo</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y divide-border">
              {sortedGroups.map((gb) => (
                <li key={gb.groupId} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">{gb.groupName}</p>
                    <p className="text-xs text-muted-foreground">
                      Rep: {gb.representativeName}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'font-semibold',
                      gb.totalBalance > 0
                        ? 'text-green-600'
                        : gb.totalBalance < 0
                          ? 'text-red-600'
                          : 'text-muted-foreground'
                    )}
                  >
                    {gb.totalBalance > 0 ? '+' : ''}
                    {formatCurrency(gb.totalBalance, currency)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Individual balances */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Balance individual</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {sortedParticipants.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Sin participantes</p>
          ) : (
            <ul className="divide-y divide-border">
              {sortedParticipants.map((pb) => (
                <li
                  key={pb.participantId}
                  className="flex items-center justify-between py-2.5"
                >
                  <span className="text-sm">{pb.participantName}</span>
                  <span
                    className={cn(
                      'font-medium',
                      pb.balance > 0
                        ? 'text-green-600'
                        : pb.balance < 0
                          ? 'text-red-600'
                          : 'text-muted-foreground'
                    )}
                  >
                    {pb.balance > 0 ? '+' : ''}
                    {formatCurrency(pb.balance, currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
});
